export type CustomMetricOperation = "direct" | "sum" | "difference" | "ratio" | "percentage";
export type CustomMetricFormat = "currency" | "currencyAbbreviated" | "percent" | "number";
export type CustomMetricVariant = "metric" | "stat" | "additional";
export type MetricColorTheme = "brand" | "green" | "blue" | "purple";

export interface CustomMetricConfig {
  metricKeyA: string;
  metricKeyB?: string;
  operation?: CustomMetricOperation;
  format?: CustomMetricFormat;
  variant?: CustomMetricVariant;
  description?: string;
  icon?: string;
  color?: MetricColorTheme;
}

const METRIC_LABELS: Record<string, string> = {
  totalCashCollected: "Cash Collected",
  totalRevenue: "Revenue",
  closeRate: "Close Rate",
  showRate: "Show Rate",
  callsDue: "Calls Due",
  callsTaken: "Calls Taken",
  callsClosed: "Calls Closed",
  avgCashPerCall: "Avg. Cash / Call",
  avgCashPerClose: "Avg. Cash / Close",
  newCashCollected: "New Cash Collected",
  mrrCash: "MRR",
  totalGrossRevenue: "Total Gross Revenue",
  cashCollected: "Cash Collected",
  closerPayouts: "Closer Payouts",
  setterPayouts: "Setter Payouts",
  takeHome: "My Take-Home",
  totalExpenses: "Total Expenses",
  netProfit: "Net Profit",
};

export function formatMetricLabel(key: string): string {
  return METRIC_LABELS[key]
    ?? key.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

export function computeCustomMetricValue(
  metrics: Record<string, number>,
  config: Pick<CustomMetricConfig, "metricKeyA" | "metricKeyB" | "operation">,
): number {
  const a = metrics[config.metricKeyA] ?? 0;
  const b = config.metricKeyB ? (metrics[config.metricKeyB] ?? 0) : 0;
  const operation = config.operation ?? "direct";

  switch (operation) {
    case "sum":
      return a + b;
    case "difference":
      return a - b;
    case "ratio":
      return b === 0 ? 0 : a / b;
    case "percentage":
      return b === 0 ? 0 : (a / b) * 100;
    case "direct":
    default:
      return a;
  }
}

export function formatMetricValue(value: number, format: CustomMetricFormat = "number"): string {
  switch (format) {
    case "currency":
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      }).format(value);
    case "currencyAbbreviated":
      if (Math.abs(value) >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
      if (Math.abs(value) >= 1000) return `$${(value / 1000).toFixed(1)}K`;
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      }).format(value);
    case "percent":
      return `${value.toFixed(1)}%`;
    case "number":
    default:
      return new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 }).format(value);
  }
}
